import { useEffect, useRef } from "react";
import { NODES, EDGES, edgeGeometry } from "@/components/story/SystemMap";
import { prefersReducedMotion } from "@/lib/motion";

/**
 * Hero visual, layer 2: the traffic on the wires.
 *
 * Canvas 2D, not WebGL. three.js used to draw this layer and it has been
 * removed: see the notes below and LEARNINGS.
 *
 * What this layer does, in order:
 *
 *   1. Starts as scattered specks, the same information before anyone
 *      organised it.
 *   2. Converges onto the exact curves the SVG draws, left column first, so
 *      the assembly reads left to right and lands ON the hairline rather than
 *      near it.
 *   3. Thins out, and what remains keeps travelling along the edges toward
 *      the outcome. The diagram is then a thing that runs, not a picture of
 *      one.
 *
 * Why Canvas 2D and not three.js:
 *
 *   The morph was the only thing that needed six thousand points, and the
 *   morph was what failed: 3px square point sprites with normal blending,
 *   jittered off the line, produced a furry band where the SVG had a 1px
 *   hairline, and the SVG was faded out at the same moment. Directional flow
 *   needs a few hundred marks, not thousands, and a 2D arc is a perfect
 *   circle with real antialiasing at device-pixel resolution. It also shares
 *   the SVG's coordinate system exactly, so nothing can drift out of
 *   register with the labels and the frame. The cost is roughly 1.5 kB in
 *   the main bundle against a 130 kB three.js chunk.
 *
 * Still deliberately absent: bloom, additive blending, chromatic aberration.
 * All three add light and are built for dark scenes. On #F7F4EF they wash
 * out to nothing. On a light editorial surface precision is the premium
 * signal, not glow.
 */

const COUNT = 560;
const CONVERGE = 1.0; /* seconds, per particle */
const INK = "22,19,15";
const COBALT = "43,63,240";

const byId = (id) => NODES.find((n) => n.id === id);

/** Sample one edge into a polyline with cumulative arc length. */
function sampleEdge(a, b) {
  const g = edgeGeometry(a, b);
  const pts = [];
  const STEPS = 60;
  for (let i = 0; i <= STEPS; i++) {
    const t = i / STEPS;
    if (g.vertical) {
      pts.push([g.x1 + (g.x2 - g.x1) * t, g.y1 + (g.y2 - g.y1) * t]);
    } else {
      const mt = 1 - t;
      const x = mt * mt * mt * g.x1 + 3 * mt * mt * t * g.mx + 3 * mt * t * t * g.mx + t * t * t * g.x2;
      const y = mt * mt * mt * g.y1 + 3 * mt * mt * t * g.y1 + 3 * mt * t * t * g.y2 + t * t * t * g.y2;
      pts.push([x, y]);
    }
  }
  const cum = [0];
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i][0] - pts[i - 1][0];
    const dy = pts[i][1] - pts[i - 1][1];
    cum.push(cum[i - 1] + Math.hypot(dx, dy));
  }
  const len = cum[cum.length - 1];
  return {
    pts,
    cum,
    len,
    depth: a.col,
    core: a.col >= 1,
    endsAtOutcome: b.col === 3,
    end: [b.x, b.y],
    at(u) {
      const d = u * len;
      let lo = 0;
      let hi = cum.length - 1;
      while (lo < hi - 1) {
        const mid = (lo + hi) >> 1;
        if (cum[mid] <= d) lo = mid;
        else hi = mid;
      }
      const span = cum[hi] - cum[lo] || 1;
      const f = (d - cum[lo]) / span;
      return [
        this.pts[lo][0] + (this.pts[hi][0] - this.pts[lo][0]) * f,
        this.pts[lo][1] + (this.pts[hi][1] - this.pts[lo][1]) * f,
      ];
    },
  };
}

const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
/* ink -> cobalt, as a channel triple for rgba(). */
const mix = (k) =>
  `${Math.round(22 + 21 * k)},${Math.round(19 + 44 * k)},${Math.round(15 + 225 * k)}`;
const smooth = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export default function ParticleField({ onReady }) {
  const host = useRef(null);

  useEffect(() => {
    const el = host.current;
    if (!el || prefersReducedMotion()) return undefined;

    const canvas = document.createElement("canvas");
    canvas.className = "hero-visual__flowcanvas";
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    el.appendChild(canvas);

    const edges = EDGES.map(([a, b]) => sampleEdge(byId(a), byId(b)));
    const total = edges.reduce((s, e) => s + e.len, 0);

    /* Particles are handed out in proportion to edge length, so the dotted
       line has the same density everywhere rather than clumping on the short
       runs. */
    const parts = [];
    let idx = 0;
    edges.forEach((e, ei) => {
      const n = Math.max(9, Math.round((e.len / total) * COUNT));
      for (let i = 0; i < n; i++) {
        const rnd = Math.random();
        parts.push({
          e: ei,
          u: (i + rnd * 0.9) / n,
          /* A constant speed in diagram units: long runs take longer, which
             is how a real flow behaves. */
          v: (34 + Math.random() * 16) / e.len,
          /* Left column assembles first. The stagger is what makes the
             picture resolve rather than appear. */
          delay: 0.1 + e.depth * 0.3 + Math.random() * 0.38,
          r: (e.core ? 1.45 : 1.15) + Math.random() * 0.35,
          a: e.core ? 0.85 : 0.6,
          keep: idx % 5 < 2,
          sx: 18 + Math.pow(Math.random(), 0.72) * 500,
          sy: 26 + Math.random() * 388,
        });
        idx++;
      }
    });

    const pulses = [];

    let w = 0;
    let h = 0;
    let dpr = 1;
    let scale = 1;
    const resize = () => {
      const cw = el.clientWidth || 1;
      const ch = el.clientHeight || 1;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.round(cw * dpr);
      h = Math.round(ch * dpr);
      canvas.width = w;
      canvas.height = h;
      /* The SVG is 640x440 with width:100%, height:auto, so the canvas box
         and the viewBox always share an aspect ratio. One scalar aligns the
         two coordinate systems. */
      scale = w / 640;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    /* Stop rendering entirely when the hero is off screen. A reader halfway
       down the page should not be spending battery on a decoration they
       cannot see. */
    let visible = true;
    const io = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }, { threshold: 0 });
    io.observe(el);

    let raf = 0;
    let announced = false;
    const t0 = performance.now();
    let last = t0;

    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!visible || document.hidden) return;
      const t = (now - t0) / 1000;

      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        const q = Math.min(1, Math.max(0, (t - p.delay) / CONVERGE));
        const e = edges[p.e];

        if (q >= 1) {
          /* Settled: hold for a beat, then travel. */
          const flowT = t - (p.delay + CONVERGE + 0.3);
          if (flowT > 0) {
            p.u += p.v * dt;
            if (p.u >= 1) {
              p.u -= 1;
              if (p.keep && e.endsAtOutcome && pulses.length < 3) {
                pulses.push({ x: e.end[0], y: e.end[1], born: t });
              }
            }
          }
          /* Most of the crowd leaves once the shape is made. What stays is
             traffic, not a fur coat on the line. */
          if (!p.keep) {
            const fade = 1 - smooth(0, 0.9, t - (p.delay + CONVERGE + 0.45));
            if (fade <= 0.001) continue;
            p.fade = fade;
          } else {
            p.fade = 1;
          }
        } else {
          p.fade = 1;
        }

        const [tx, ty] = e.at(p.u);
        const k = easeOutQuint(q);
        const x = p.sx + (tx - p.sx) * k;
        const y = p.sy + (ty - p.sy) * k;

        /* Fade in off the source node and out into the target node, so a
           courier emerges and arrives instead of popping. */
        const ends = q >= 1 ? smooth(0, 0.07, p.u) * (1 - smooth(0.9, 1, p.u)) : 1;
        const alpha = p.a * p.fade * ends * (0.35 + 0.65 * q);
        if (alpha <= 0.01) continue;

        ctx.beginPath();
        ctx.arc(x * scale, y * scale, p.r * dpr, 0, 6.2832);
        /* Colour arrives with order: every speck starts ink, and only the
           ones that belong to the organised half turn cobalt as they land. */
        ctx.fillStyle = `rgba(${e.core ? mix(k) : INK},${alpha.toFixed(3)})`;
        ctx.fill();
      }

      /* A ring at the outcome when something lands there. One hairline, no
         glow: the point is that work arrives, not that it sparkles. */
      for (let i = pulses.length - 1; i >= 0; i--) {
        const age = t - pulses[i].born;
        if (age > 1.1) { pulses.splice(i, 1); continue; }
        const f = age / 1.1;
        ctx.beginPath();
        ctx.arc(pulses[i].x * scale, pulses[i].y * scale, (10 + f * 11) * scale, 0, 6.2832);
        ctx.strokeStyle = `rgba(${COBALT},${(0.22 * (1 - f)).toFixed(3)})`;
        ctx.lineWidth = Math.max(1, dpr * 0.9);
        ctx.stroke();
      }

      if (!announced) {
        announced = true;
        if (typeof onReady === "function") onReady();
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, [onReady]);

  return <div ref={host} className="absolute inset-0" aria-hidden="true" />;
}
